import { useContext } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import ContrastIcon from "@mui/icons-material/ContrastOutlined";
import { ThemeContext } from "../../providers/ThemeProvider";
import { ANIMATIONS, NAV_ITEM_HEIGHT } from "../../styles/layout";

/**
 * Segmented light/dark switch pinned to the sidebar footer.
 * Collapses to a single icon button on the mini rail.
 */
export const ThemeToggle = ({ open }: { open: boolean }) => {
  const { isDarkMode, toggleDarkMode } = useContext(ThemeContext);

  const setMode = (dark: boolean) => {
    if (dark !== isDarkMode) toggleDarkMode();
  };

  if (!open) {
    return (
      <Tooltip
        title={isDarkMode ? "Zu hellem Design wechseln" : "Zu dunklem Design wechseln"}
        placement="right"
      >
        <Box
          component="button"
          type="button"
          onClick={toggleDarkMode}
          aria-label="Design wechseln"
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: NAV_ITEM_HEIGHT,
            height: NAV_ITEM_HEIGHT,
            mx: "auto",
            borderRadius: 2,
            border: "none",
            cursor: "pointer",
            color: theme.palette.text.secondary,
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: theme.palette.surface.hover },
          })}
        >
          <ContrastIcon sx={{ fontSize: "1.125rem" }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.25,
        minHeight: NAV_ITEM_HEIGHT,
      }}
    >
      <Box
        sx={{
          width: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        <ContrastIcon sx={{ fontSize: "1.125rem" }} />
      </Box>
      <Typography
        component="span"
        sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}
      >
        Theme
      </Typography>

      <Box
        role="group"
        aria-label="Design wechseln"
        sx={(theme) => ({
          ml: "auto",
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          p: 0.375,
          borderRadius: 999,
          backgroundColor: theme.palette.surface.inset,
          border: `1px solid ${theme.palette.divider}`,
        })}
      >
        <ModeButton
          active={!isDarkMode}
          label="Helles Design"
          onClick={() => setMode(false)}
        >
          <LightModeIcon sx={{ fontSize: "0.9375rem" }} />
        </ModeButton>
        <ModeButton
          active={isDarkMode}
          label="Dunkles Design"
          onClick={() => setMode(true)}
        >
          <DarkModeIcon sx={{ fontSize: "0.9375rem" }} />
        </ModeButton>
      </Box>
    </Box>
  );
};

const ModeButton = ({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Tooltip title={label}>
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 26,
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        transition: `background-color ${ANIMATIONS.fast}, color ${ANIMATIONS.fast}`,
        backgroundColor: active
          ? theme.palette.background.paper
          : "transparent",
        color: active ? theme.palette.warning.main : theme.palette.text.disabled,
        boxShadow: active ? theme.shadows[1] : "none",
        "&:hover": {
          color: active ? theme.palette.warning.main : theme.palette.text.secondary,
        },
      })}
    >
      {children}
    </Box>
  </Tooltip>
);

export default ThemeToggle;
